import { useState } from "react";
import type { CommentDto, ContentType } from "../../api/types";
import { postComment } from "../../api/content";

interface CommentFormProps {
  contentType: ContentType;
  sourceId: number;
  parentId?: number | null;
  /** Called with the created comment after a successful post. */
  onPosted: (created: CommentDto) => void;
  /** Shown for reply forms so the user can back out. */
  onCancel?: () => void;
  compact?: boolean;
}

/**
 * The comment composer. Name is optional (defaults to "Anonymous"); the body is
 * required. Used at the top of a thread for new comments and inline beneath a
 * comment for replies.
 */
export function CommentForm({
  contentType,
  sourceId,
  parentId = null,
  onPosted,
  onCancel,
  compact = false,
}: CommentFormProps) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReply = parentId != null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await postComment({
        contentType,
        sourceId,
        parentId,
        authorName: name.trim() || undefined,
        body: body.trim(),
      });
      setBody("");
      setName("");
      onPosted(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not post your comment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className={isReply ? "reply-form" : "comment-form"}
      onSubmit={handleSubmit}
    >
      {!compact && <h3>{isReply ? "Post a reply" : "Leave a comment"}</h3>}

      <div className="field">
        <label htmlFor={`name-${parentId ?? "root"}`}>Name (optional)</label>
        <input
          id={`name-${parentId ?? "root"}`}
          type="text"
          value={name}
          maxLength={80}
          placeholder="Anonymous"
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`body-${parentId ?? "root"}`}>
          {isReply ? "Your reply" : "Your comment"}
        </label>
        <textarea
          id={`body-${parentId ?? "root"}`}
          value={body}
          maxLength={5000}
          required
          rows={isReply ? 3 : 4}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      {error && <p className="field__error">{error}</p>}

      <div className="row">
        <button className="btn" type="submit" disabled={submitting || !body.trim()}>
          {submitting ? "Posting…" : isReply ? "Post reply" : "Post comment"}
        </button>
        {onCancel && (
          <button className="btn btn--ghost" type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
